import React, {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';

import { Box } from '../Box';
import { Button } from '../Button';
import { Icon } from '../Icon';
import * as styles from './BottomDrawer.styles';
import type { BottomDrawerProps } from './BottomDrawer.types';

/**
 * BottomDrawer component that slides up from the bottom on mobile
 * and displays as a centered modal on desktop.
 * Supports keyboard navigation, focus trapping, and accessibility features.
 */
export const BottomDrawer = ({
  isOpen,
  onClose,
  children,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  containerStyles: customContainerStyles,
  contentStyles: customContentStyles,
  ariaLabel,
}: BottomDrawerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousScrollY = useRef<number>(0);
  const previousActiveElement = useRef<Element | null>(null);
  const wasOpenRef = useRef<boolean>(false);

  // Handle escape key press
  const handleKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape' && onClose) {
        onClose(event as unknown as KeyboardEvent);
      }
    },
    [closeOnEscape, onClose]
  );

  // Prevent background scrolling and manage focus when drawer is open
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      // Store current scroll position and active element
      previousScrollY.current = window.scrollY;
      previousActiveElement.current = document.activeElement;

      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${previousScrollY.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Add escape key listener
      document.addEventListener('keydown', handleKeyDown);

      // Focus the drawer container for accessibility
      if (containerRef.current) {
        containerRef.current.focus();
      }
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      // Restore body scroll
      document.body.style.position = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, previousScrollY.current);

      // Restore focus to previous element
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Cleanup body styles on unmount
      document.body.style.position = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen, handleKeyDown]);

  const handleClose = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      if (onClose) {
        onClose(event);
      }
    },
    [onClose]
  );

  const handleBackdropClick = useCallback(
    (event: MouseEvent) => {
      if (closeOnOutsideClick && onClose) {
        handleClose(event);
      }
    },
    [closeOnOutsideClick, onClose, handleClose]
  );

  const handleContainerClick = useCallback((event: MouseEvent) => {
    // Prevent clicks inside the container from closing the drawer
    event.stopPropagation();
  }, []);

  if (!isOpen) {
    return null;
  }

  // Ensure we're in a browser environment for portal rendering
  if (typeof document === 'undefined') {
    return null;
  }

  const closeButton = showCloseButton && (
    <Button
      onClick={handleClose}
      css={[styles.closeButtonStyles]}
      data-testid="bottom-drawer-close-button"
      aria-label="Close drawer"
      variant="text"
      size="xs"
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        gap="var(--spacing-2)"
      >
        <Icon variant="Xmark" size="medium" />
        <span>Close</span>
      </Box>
    </Button>
  );

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <Box
        css={styles.backdropStyles}
        onClick={handleBackdropClick}
        data-testid="bottom-drawer-backdrop"
        aria-hidden="true"
      />
      {/* Drawer container */}
      <Box
        ref={containerRef}
        css={[styles.containerStyles, customContainerStyles]}
        onClick={handleContainerClick}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        data-testid="bottom-drawer"
      >
        {closeButton}

        <Box
          flex="1"
          overflow="auto"
          css={[styles.contentStyles, customContentStyles]}
        >
          {children}
        </Box>
      </Box>
    </>,
    document.body
  );
};

BottomDrawer.displayName = 'BottomDrawer';
